import type { ReactiveController, ReactiveControllerHost } from 'lit';

/**
 * <ViewportController> — THE single owner of viewport-tier matchMedia.
 *
 * WHY THIS EXISTS: the responsive spec (docs/roam/roam-responsive-pwa-spec.md
 * §6, Workstream A) draws a hard line — "container queries for components,
 * viewport tiers for the shell." Container queries handle almost everything
 * (a `cos-data-table` in a drawer has the same sizing problem on desktop as
 * on a phone, so it should adapt to ITS BOX, not the viewport — the
 * `cos-record-shell` pattern, generalized). But a genuine handful of
 * shell-level decisions — which nav chrome to render, whether a modal is a
 * centered dialog or a bottom sheet, whether a screen should even be shown
 * on this device class — legitimately depend on the viewport, not any one
 * component's container. Those decisions need ONE source of truth so no two
 * components ever disagree about what "compact" means, and so the browser
 * only pays for one set of `matchMedia` listeners no matter how many
 * components ask.
 *
 * Three tiers (mirrors the `--cos-bp-*` tokens in tokens.css — CSS custom
 * properties can't be read inside an `@media` condition, so the numbers are
 * duplicated below; keep the two in sync by hand if the scale ever changes):
 *   - compact  < 768px   (phones)
 *   - medium   768–1099px (tablets, split-screen)
 *   - expanded >= 1100px  (desktop; matches the existing shell assumptions)
 *
 * Plus the input axis, tracked independently of width (a tablet in landscape
 * can be `expanded` width AND `coarse` pointer at the same time):
 *   - coarse:    `(pointer: coarse)` — a touch-primary pointer
 *   - hoverless: `!(hover: hover)` — no persistent hover capability
 *
 * Usage — a Lit ReactiveController, same shape as AsyncController
 * (state/async.ts) / ShortcutsController (keyboard/controller.ts):
 *
 *   class CosFoo extends LitElement {
 *     private _viewport = new ViewportController(this);
 *     render() {
 *       if (this._viewport.tier === 'compact') { ... }
 *     }
 *   }
 *
 * No component ever calls `window.matchMedia` itself for a tier/pointer
 * check — go through this controller (or, for a component that only needs
 * to react to ITS OWN box size, a container query, which needs no JS at
 * all).
 *
 * MECHANICS: a module-level singleton set of `MediaQueryList`s is created
 * once, lazily, on the first `ViewportController` construction anywhere in
 * the app, and reused by every subsequent instance — there is exactly one
 * underlying browser listener per query regardless of how many components
 * hold a controller. Each controller instance subscribes itself into a
 * shared `Set` on `hostConnected` and unsubscribes on `hostDisconnected`
 * (the AsyncController/ShortcutsController cleanup precedent — a component
 * that never mounts again must not keep being notified). A change on any of
 * the shared queries recomputes every subscribed controller's public
 * properties and calls that controller's own `host.requestUpdate()` — so a
 * host's `render()` re-runs exactly like it would after any other reactive
 * property change, with no extra wiring required on the component's part.
 */

export type ViewportTier = 'compact' | 'medium' | 'expanded';

// Mirrors --cos-bp-medium-min / --cos-bp-expanded-min in tokens.css.
export const VIEWPORT_MEDIUM_MIN_PX = 768;
export const VIEWPORT_EXPANDED_MIN_PX = 1100;

/** Pure classifier — exported so tests (and anything else) can compute a
 * tier from a raw width with no DOM/matchMedia involved at all. */
export function viewportTier(width: number): ViewportTier {
	if (width < VIEWPORT_MEDIUM_MIN_PX) return 'compact';
	if (width < VIEWPORT_EXPANDED_MIN_PX) return 'medium';
	return 'expanded';
}

interface SharedQueries {
	mediumMin: MediaQueryList;
	expandedMin: MediaQueryList;
	coarse: MediaQueryList;
	hover: MediaQueryList;
}

// Module-level singleton — see the "MECHANICS" doc comment above. Created
// lazily so importing this module in a non-browser context (SSR, a plain
// node test that never constructs a controller) never touches `window`.
let _shared: SharedQueries | null = null;
const _subscribers = new Set<ViewportController>();

/** Test-only — clear the shared singleton so vitest cases can observe
 * "first construction" behavior regardless of what ran before them in the
 * same file (the module-level cache otherwise persists across every test
 * in a run, which only stayed invisible because this test file's one test
 * asserting first-construction happened to be listed first in the file —
 * fragile under `--sequence.shuffle`). */
export function _resetForTests(): void {
	_shared = null;
	_subscribers.clear();
}

function _hasMatchMedia(): boolean {
	return typeof window !== 'undefined' && typeof window.matchMedia === 'function';
}

function _notifyAll(): void {
	for (const controller of _subscribers) controller._recompute();
}

function _ensureShared(): SharedQueries | null {
	if (_shared) return _shared;
	if (!_hasMatchMedia()) return null;
	_shared = {
		mediumMin: window.matchMedia(`(min-width: ${VIEWPORT_MEDIUM_MIN_PX}px)`),
		expandedMin: window.matchMedia(`(min-width: ${VIEWPORT_EXPANDED_MIN_PX}px)`),
		coarse: window.matchMedia('(pointer: coarse)'),
		hover: window.matchMedia('(hover: hover)'),
	};
	_shared.mediumMin.addEventListener('change', _notifyAll);
	_shared.expandedMin.addEventListener('change', _notifyAll);
	_shared.coarse.addEventListener('change', _notifyAll);
	_shared.hover.addEventListener('change', _notifyAll);
	return _shared;
}

export class ViewportController implements ReactiveController {
	host: ReactiveControllerHost;

	/** The active viewport tier. Defaults to 'expanded' — the desktop-first
	 * fallback for an environment with no `matchMedia` (SSR, a bare test). */
	tier: ViewportTier = 'expanded';
	/** True when the primary pointer is touch/coarse. */
	coarse = false;
	/** True when the device has no persistent hover capability. */
	hoverless = false;

	constructor(host: ReactiveControllerHost) {
		this.host = host;
		host.addController(this);
	}

	hostConnected(): void {
		_ensureShared();
		_subscribers.add(this);
		this._recompute(/* silent */ true);
	}

	hostDisconnected(): void {
		_subscribers.delete(this);
	}

	/** @internal — invoked by the shared change notifier, or on connect to
	 * seed the initial values. Public only so the module-level notifier can
	 * reach it; not part of the component-facing API. */
	_recompute(silent = false): void {
		const q = _ensureShared();
		const nextTier: ViewportTier = q
			? !q.mediumMin.matches
				? 'compact'
				: q.expandedMin.matches
					? 'expanded'
					: 'medium'
			: 'expanded';
		const nextCoarse = q ? q.coarse.matches : false;
		const nextHoverless = q ? !q.hover.matches : false;

		const changed = nextTier !== this.tier || nextCoarse !== this.coarse || nextHoverless !== this.hoverless;
		this.tier = nextTier;
		this.coarse = nextCoarse;
		this.hoverless = nextHoverless;

		if (changed && !silent) this.host.requestUpdate();
	}
}
